import { safeFormatDate, safeFormatDistance, safeCreateDate } from "../../../utils/dateUtils";

export const STATUS_STYLE_MAP = {
    ready: 'bg-blue-50 border-blue-200 text-blue-800',
    notReady: 'bg-amber-50 border-amber-200 text-amber-800',
    review: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    rejected: 'bg-red-50 border-red-200 text-red-700',
    approved: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    started: 'bg-green-50 border-green-200 text-green-700',
    default: 'bg-gray-50 border-gray-200 text-gray-700',
} as const;

export type ProjectStatusTone = keyof typeof STATUS_STYLE_MAP;

export interface ProjectStatusInfo {
    label: string;
    description?: string;
    tone: ProjectStatusTone;
}

/** Extract a user id from a value that may be an ObjectId string, a populated user object, or null. */
const extractUserId = (value: any): string | null => {
    if (value == null) return null;
    if (typeof value === 'string') {
        const trimmed = value.trim();
        return trimmed || null;
    }
    if (typeof value === 'object') {
        const id = value._id ?? value.id;
        if (id == null) return null;
        const s = typeof id === 'string' ? id.trim() : String(id);
        return s || null;
    }
    return null;
};

const collectUserIds = (value: any, sink: Set<string>): void => {
    if (Array.isArray(value)) {
        for (const v of value) {
            const id = extractUserId(v);
            if (id) sink.add(id);
        }
        return;
    }
    const id = extractUserId(value);
    if (id) sink.add(id);
};

export const countAssignedResources = (assignedResources: any): number => {
    if (!assignedResources) return 0;
    if (Array.isArray(assignedResources)) {
        const ids = new Set<string>();
        for (const v of assignedResources) {
            const id = extractUserId(v);
            if (id) ids.add(id);
        }
        // Fall back to plain truthy length when ids couldn't be resolved (e.g. legacy summary numbers).
        return ids.size > 0 ? ids.size : assignedResources.filter(Boolean).length;
    }

    const ids = new Set<string>();
    collectUserIds(assignedResources.projectManager, ids);
    collectUserIds(assignedResources.projectHead, ids);
    collectUserIds(assignedResources.siteSupervisors, ids);
    collectUserIds(assignedResources.siteEngineers, ids);
    collectUserIds(assignedResources.additionalResources, ids);

    if (ids.size > 0) return ids.size;

    // Legacy fallback: arrays may contain raw numbers in older responses.
    let count = 0;
    const numericArrayLen = (v: any) =>
        Array.isArray(v) ? v.filter(Boolean).length : typeof v === 'number' && Number.isFinite(v) ? v : 0;
    if (assignedResources.projectManager) count += 1;
    if (assignedResources.projectHead) count += 1;
    count += numericArrayLen(assignedResources.siteSupervisors);
    count += numericArrayLen(assignedResources.siteEngineers);
    count += numericArrayLen(assignedResources.additionalResources);
    return count;
};

/** Coerce a value into a non-negative integer count, or return null if it isn't a usable number. */
const asCount = (value: any): number | null => {
    if (value == null) return null;
    if (typeof value === 'number' && Number.isFinite(value)) return Math.max(0, Math.floor(value));
    if (typeof value === 'string') {
        const n = Number(value);
        return Number.isFinite(n) ? Math.max(0, Math.floor(n)) : null;
    }
    if (Array.isArray(value)) return value.filter(Boolean).length;
    return null;
};

export const getTotalAssetCount = (project: any): number => {
    // Prefer the backend-authoritative `totalAssets` field (active project assets count).
    const primary = asCount(project?.totalAssets);
    if (primary !== null) return primary;

    const fallbacks: any[] = [
        project?.assetCount,
        project?.assetsCount,
        project?.summary?.totalAssets,
        project?.summary?.assetCount,
        project?.overview?.totalAssets,
        project?.overview?.assetCount,
        project?.requestData?.totalAssets,
        project?.assignmentStats?.totalAssets,
        project?.assignmentSummary?.totalAssets,
        project?.projectAssets,
        project?.assets,
    ];
    for (const v of fallbacks) {
        const n = asCount(v);
        if (n !== null) return n;
    }
    return 0;
};

export const getNormalizedApprovalStatus = (project: any): string => {
    const statusCandidates = [
        project?.approvalStatus,
        project?.projectApprovalStatus,
        project?.projectStatus,
        project?.status,
        project?.overview?.approvalStatus,
        project?.overview?.status,
        project?.approval?.status,
        project?.workflowStatus,
    ];

    for (const candidate of statusCandidates) {
        if (candidate !== undefined && candidate !== null && candidate !== '') {
            return candidate.toString().trim().toLowerCase();
        }
    }

    return '';
};

export const getTotalEquipmentCount = (project: any): number => {
    const primary = asCount(project?.totalEquipment);
    if (primary !== null) return primary;

    const fallbacks: any[] = [
        project?.equipmentCount,
        project?.totalEquipmentCount,
        project?.totalEquipmentAssigned,
        project?.overview?.totalEquipment,
        project?.overview?.assignedEquipment,
        project?.requestData?.totalEquipment,
        project?.assignmentStats?.totalEquipment,
        project?.assignmentStats?.assignedEquipment,
        project?.summary?.totalEquipment,
        project?.summary?.assignedEquipment,
        project?.equipment,
        project?.assetEquipment,
    ];
    for (const v of fallbacks) {
        const n = asCount(v);
        if (n !== null) return n;
    }
    return 0;
};

export const getTotalModuleCount = (project: any): number => {
    const primary = asCount(project?.totalModules);
    if (primary !== null) return primary;

    const fallbacks: any[] = [
        project?.moduleCount,
        project?.pvModuleCount,
        project?.totalPvModules,
        project?.equipmentSummary?.moduleCount,
        project?.summary?.moduleCount,
        project?.summary?.totalModules,
        project?.overview?.moduleCount,
        project?.overview?.totalModules,
        project?.assignmentStats?.totalModules,
        project?.assignmentSummary?.totalModules,
    ];
    for (const v of fallbacks) {
        const n = asCount(v);
        if (n !== null) return n;
    }
    return 0;
};

export const getTotalResourceCount = (project: any): number => {
    if (!project) return 0;

    // Build a deduplicated set of user ids across every team-relevant field on the project.
    // This prevents the same person being counted twice when they appear as both `manager`
    // and `assignedResources.projectManager`, etc.
    const ids = new Set<string>();
    collectUserIds(project?.manager, ids);
    collectUserIds(project?.projectManager, ids); // some legacy responses use top-level field
    collectUserIds(project?.projectHead, ids);

    const ar = project?.assignedResources;
    if (ar && !Array.isArray(ar)) {
        collectUserIds(ar.projectManager, ids);
        collectUserIds(ar.projectHead, ids);
        collectUserIds(ar.siteSupervisors, ids);
        collectUserIds(ar.siteEngineers, ids);
        collectUserIds(ar.additionalResources, ids);
    } else if (Array.isArray(ar)) {
        collectUserIds(ar, ids);
    }

    if (ids.size > 0) return ids.size;

    // Fallback for legacy/summary responses where only counts (no id arrays) are returned.
    const numericFallbacks: any[] = [
        project?.resourceCount,
        project?.totalResources,
        project?.assignedResourceCount,
        project?.assignmentStats?.totalResources,
        project?.assignmentStats?.totalAssignees,
        project?.assignmentSummary?.totalResources,
        project?.assignmentSummary?.totalAssignees,
        project?.overview?.assignedResources,
        project?.overview?.totalResources,
        project?.summary?.resourceCount,
        project?.summary?.assignedResources,
    ];
    let total = countAssignedResources(ar);
    for (const v of numericFallbacks) {
        if (typeof v === 'number' && Number.isFinite(v)) {
            total = Math.max(total, v);
        } else if (typeof v === 'string') {
            const parsed = Number(v);
            if (Number.isFinite(parsed)) total = Math.max(total, parsed);
        }
    }
    return total;
};

export const getProjectStatusInfo = (project: any): ProjectStatusInfo => {
    const approvalStatus = getNormalizedApprovalStatus(project);
    const totalAssets = getTotalAssetCount(project);
    const totalEquipment = getTotalEquipmentCount(project);
    const hasEquipment = totalEquipment > 0;
    const resourceCount = getTotalResourceCount(project);
    const hasResources = resourceCount > 0;

    // Draft: show "Draft" only when no assets attached. If assets/equipment/resources are added, show ready/pending etc.
    if (totalAssets === 0) {
        return {
            label: 'Draft',
            description: 'Add assets to get started',
            tone: 'notReady',
        };
    }

    // Ready to send for review: has assets+equipment+resources but not yet sent (draft/not_required). Show this before "Project started".
    const isReadyToSend = (approvalStatus === 'draft' || approvalStatus === 'not_required' || !approvalStatus) && hasEquipment && hasResources;
    if (isReadyToSend) {
        const parts: string[] = [];
        if (totalEquipment) parts.push(`${totalEquipment} equipment`);
        if (resourceCount) parts.push(`${resourceCount} ${resourceCount === 1 ? 'resource' : 'resources'}`);
        return {
            label: 'Ready to send for review',
            description: parts.length ? parts.join(' · ') : undefined,
            tone: 'ready',
        };
    }

    const startDateRaw =
        project?.actualStartDate ||
        project?.startDate ||
        project?.plannedStartDate ||
        project?.projectStartDate ||
        project?.expectedStartDate;

    const startDate = safeCreateDate(startDateRaw);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let hasStarted = false;
    if (startDate) {
        const normalizedStart = new Date(startDate);
        normalizedStart.setHours(0, 0, 0, 0);
        hasStarted = normalizedStart.getTime() <= today.getTime();
    }

    const startDateLabel = startDate ? safeFormatDate(startDate, "MMM dd, yyyy") : null;
    const startedDistance = startDate ? safeFormatDistance(startDate, { addSuffix: true }) : null;

    if (approvalStatus === 'partially_approved') {
        return {
            label: 'Review partially approved – finish pending items',
            description: 'Resolve outstanding approvals to move forward',
            tone: 'review',
        };
    }

    if (approvalStatus === 'not_required') {
        // Check if project has started based on start date
        if (hasStarted) {
            return {
                label: 'Project started',
                description: startedDistance ? `Started ${startedDistance}` : startDateLabel ? `Started on ${startDateLabel}` : 'Project is now active',
                tone: 'started',
            };
        }

        // Project hasn't started yet
        if (startDate) {
            return {
                label: 'Setup in Progress',
                description: startDateLabel ? `Scheduled to start on ${startDateLabel}` : 'Complete asset assignments before start date',
                tone: 'notReady',
            };
        }

        // No start date set
        return {
            label: 'Setup in Progress',
            description: 'Set project start date and complete asset assignments',
            tone: 'notReady',
        };
    }

    if (approvalStatus === 'approved') {
        if (hasStarted) {
            return {
                label: 'Project started',
                description: startedDistance ? `Started ${startedDistance}` : startDateLabel ? `Started on ${startDateLabel}` : undefined,
                tone: 'started',
            };
        }
        return {
            label: 'Project approved and ready to start',
            description: startDateLabel ? `Scheduled start ${startDateLabel}` : 'Set a start date to begin execution',
            tone: 'approved',
        };
    }

    if (approvalStatus === 'rejected') {
        return {
            label: 'Rejected by Head - Needs Modification',
            description: 'Update project details and resubmit for approval',
            tone: 'rejected',
        };
    }

    if (approvalStatus === 'pending_approval' || approvalStatus === 'in_review' || approvalStatus === 'under_review') {
        const isResubmission = project?.rejectionReason || project?.projectLevelApproverComments;
        return {
            label: isResubmission ? 'Resubmitted for Review' : 'Pending Approval',
            description: isResubmission ? 'Awaiting review of updated project details' : 'Awaiting head approval to proceed',
            tone: 'review',
        };
    }

    if (hasEquipment && hasResources) {
        const parts: string[] = [];
        if (totalEquipment) parts.push(`${totalEquipment} equipment`);
        if (resourceCount) parts.push(`${resourceCount} ${resourceCount === 1 ? 'resource' : 'resources'}`);
        return {
            label: 'Ready to send for review',
            description: parts.length ? parts.join(' · ') : undefined,
            tone: 'ready',
        };
    }

    const missingParts: string[] = [];
    if (!hasEquipment) missingParts.push('equipment');
    if (!hasResources) missingParts.push('resources');
    const missingText = missingParts.length > 1 ? `${missingParts[0]} and ${missingParts[1]}` : (missingParts[0] || 'key details');

    return {
        label: 'Not ready for review',
        description: `Complete remaining setup – add ${missingText}.`,
        tone: 'notReady',
    };
};

// Check if project needs approval or resubmission (not approved yet)
export const needsApproval = (project: any) => {
    const status = project.approvalStatus;
    // Show send button ONLY for draft, rejected, no status, or pending_approval
    // DO NOT show for approved or partially_approved - project is already approved/ready to start
    return status === 'draft' || status === 'rejected' || status === 'pending_approval' || !status;
};

/** Tab ids matching create/edit form steps */
export type ProjectInfoTabId = 'basic' | 'team' | 'location' | 'review';

function getProjectManagerId(project: any): string {
    if (!project) return '';
    const raw = project.manager ?? project.assignedResources?.projectManager;
    if (typeof raw === 'string') return raw.trim();
    if (raw && typeof raw === 'object' && (raw._id || raw.id)) return String(raw._id || raw.id);
    return '';
}

function toId(value: any): string {
    if (value == null) return '';
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'object' && (value._id || value.id)) return String(value._id || value.id);
    return '';
}

/**
 * Returns true if project has required info (basic + team) so user can open assets/equipment.
 * Same required fields as create form: basic (id, name, type, customer, startDate) + team (manager).
 */
export function isProjectInfoComplete(project: any): boolean {
    if (!project) return false;
    const hasBasic =
        !!toId(project.projectId || project.code) &&
        !!String(project.projectName || project.name || '').trim() &&
        !!toId(project.projectTypeId || project.projectType) &&
        !!toId(project.customerId || project.customer) &&
        !!(project.startDate || project.plannedStartDate || project.actualStartDate);
    const hasManager = !!getProjectManagerId(project);
    return hasBasic && hasManager;
}

/**
 * Returns the first tab that has missing required info, or null if complete.
 */
export function getFirstIncompleteProjectInfoTab(project: any): ProjectInfoTabId | null {
    if (!project) return 'basic';
    if (!toId(project.projectId || project.code) || !String(project.projectName || project.name || '').trim() ||
        !toId(project.projectTypeId || project.projectType) || !toId(project.customerId || project.customer) ||
        !(project.startDate || project.plannedStartDate || project.actualStartDate)) return 'basic';
    if (!getProjectManagerId(project)) return 'team';
    return null;
}
